import type { ReactNode } from "react"

import { cn } from "@/lib/utils"

export type EntityCardField = {
  label: string
  value: ReactNode
}

export function EntityCard({
  title,
  subtitle,
  leading,
  trailing,
  fields = [],
  onClick,
  className,
}: {
  title: ReactNode
  subtitle?: ReactNode
  leading?: ReactNode
  trailing?: ReactNode
  fields?: EntityCardField[]
  onClick?: () => void
  className?: string
}) {
  const content = (
    <>
      <div className="flex min-w-0 items-start gap-3">
        {leading ? <div className="shrink-0 pt-0.5">{leading}</div> : null}
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-foreground">{title}</div>
          {subtitle ? <div className="mt-0.5 truncate text-xs text-muted-foreground">{subtitle}</div> : null}
        </div>
        {trailing ? <div className="shrink-0">{trailing}</div> : null}
      </div>
      {fields.length ? (
        <div className="mt-3 grid grid-cols-2 gap-3">
          {fields.map((field) => (
            <div key={field.label} className="min-w-0">
              <div className="text-[11px] font-medium uppercase text-muted-foreground">{field.label}</div>
              <div className="mt-1 truncate text-xs text-foreground">{field.value}</div>
            </div>
          ))}
        </div>
      ) : null}
    </>
  )

  if (onClick) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            onClick()
          }
        }}
        className={cn("w-full rounded-lg border bg-card p-3 text-left text-card-foreground", className)}
      >
        {content}
      </div>
    )
  }

  return <div className={cn("rounded-lg border bg-card p-3 text-card-foreground", className)}>{content}</div>
}
